import React from "react";
import { CreditCard, Crown, Calendar, Star, ShieldCheck } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/hooks/useAuth";
import Card from "../../common/Card";
import Button from "../../common/Button";

const Membership = () => {
  const { user } = useAuth();

  const membership =
    typeof user?.membership === "object"
      ? user?.membership?.plan || "Premium Rider"
      : user?.membership || "Premium Rider";
  const renewalDate = user?.renewalDate || "2026-01-01";
  const perks = [
    {
      id: 1,
      icon: <Star className="w-5 h-5" />,
      text: "Access to verified routes",
    },
    {
      id: 2,
      icon: <ShieldCheck className="w-5 h-5" />,
      text: "Exclusive club events",
    },
    {
      id: 3,
      icon: <Crown className="w-5 h-5" />,
      text: "Priority route uploads",
    },
    {
      id: 4,
      icon: <Calendar className="w-5 h-5" />,
      text: "Early event registration",
    },
  ];

  return (
    <section className="space-y-10">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
          Membership
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Manage your plan and explore membership perks
        </p>
      </div>

      {/* Membership Card */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-gradient-to-br from-[#1e8a3b] to-[#114a1f] dark:from-[#166534] dark:to-[#052e16] text-white rounded-3xl shadow-lg shadow-green-900/20 border border-white/10 p-8 flex flex-col sm:flex-row justify-between items-center"
      >
        <div className="flex items-center gap-4">
          <div className="p-4 bg-white/20 rounded-full">
            <CreditCard className="w-8 h-8" />
          </div>
          <div>
            <h3 className="text-2xl font-bold">{membership}</h3>
            <p className="text-sm opacity-90">Active until {renewalDate}</p>
          </div>
        </div>

        <div className="mt-6 sm:mt-0">
          <Button
            variant="secondary"
            size="sm"
            className="bg-white text-green-600 border-none hover:bg-green-50"
          >
            Manage Plan
          </Button>
        </div>
      </motion.div>

      {/* Perks Section */}
      <Card
        title="Membership Perks"
        className="bg-white dark:bg-gray-800 shadow-sm transition-colors duration-300"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {perks.map((perk) => (
            <div
              key={perk.id}
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
            >
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                {perk.icon}
              </div>
              <span className="text-sm text-gray-700 dark:text-gray-200">
                {perk.text}
              </span>
            </div>
          ))}
        </div>
      </Card>

      {/* Upgrade Banner */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Card className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-center transition-colors duration-300">
          <h3 className="text-lg font-semibold text-green-700 dark:text-green-400 mb-2">
            Want to unlock more features?
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
            Upgrade to{" "}
            <span className="font-medium text-green-700 dark:text-green-400">
              Koset Elite
            </span>{" "}
            and enjoy exclusive events, early access to route launches, and club
            badges.
          </p>
          <Button variant="primary">Upgrade Now</Button>
        </Card>
      </motion.div>
    </section>
  );
};

export default Membership;
